import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BotService } from 'src/app/core/services/bot.service';
import { ChatbotWidgetConfiguration, ResponseGetBotInterface } from 'src/app/models/bot.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { GlobalObservableService } from 'src/app/core/services/global-observable.service';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface WidgetStyles {
  topBarColor: string;
  userBubbleColor: string;
  botBubbleColor: string;
  buttonColor: string;
}

@Component({
  selector: 'globot-widget-config',
  templateUrl: './widget-config.component.html',
  styleUrls: ['./widget-config.component.scss'],
})
export class WidgetConfigComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('iframe') iframe: ElementRef | undefined;

  widgetUrl = environment.widgetUrl;
  demoTargetOrigin = environment.demoTargetOrigin;
  private botSubscription!: Subscription;
  private destroy$ = new Subject<void>();

  colorList = [
    { value: '#7B5CF5', name: 'Violeta' },
    { value: '#27283B', name: 'Carbón' },
    { value: '#CE4037', name: 'Rojo' },
    { value: '#477D46', name: 'Verde' },
    { value: '#2173DC', name: 'Azul' },
    { value: '#B869DE', name: 'Magenta' },
  ];

  get colorRows() {
    const rows = [];
    for (let i = 0; i < this.colorList.length; i += 2) {
      rows.push(this.colorList.slice(i, i + 2));
    }
    return rows;
  }

  showiframe = true;
  isLoading = false;
  selectedColor: string = '#7B5CF5';
  isIframeLoading = true;
  configExpanded = false;

  welcomeEnabled = false;
  welcomeMessage = '';
  welcomeMessageError = false;

  justSaved = false;
  private justSavedTimeout: any;

  private savedState = {
    name: '',
    identifier: '',
    color: '#7B5CF5',
    welcomeEnabled: false,
    welcomeMessage: '',
  };

  get isDirty(): boolean {
    const name = this.chatbotForm?.get('name')?.value?.trim() ?? '';
    const identifier = this.chatbotForm?.get('identifier')?.value?.trim() ?? '';
    return (
      name !== this.savedState.name ||
      identifier !== this.savedState.identifier ||
      this.selectedColor !== this.savedState.color ||
      this.welcomeEnabled !== this.savedState.welcomeEnabled ||
      this.welcomeMessage !== this.savedState.welcomeMessage
    );
  }

  selectColor(color: string) {
    this.selectedColor = color;
    this.iframe?.nativeElement.contentWindow.postMessage({ color: color }, this.demoTargetOrigin);
  }

  styles: WidgetStyles = {
    topBarColor: '#4A90E2',
    userBubbleColor: '#E3F2FD',
    botBubbleColor: '#F5F5F5',
    buttonColor: '#4A90E2',
  };

  tempStyles: WidgetStyles = { ...this.styles };

  styleKeys = Object.keys(this.styles);

  botId;
  chatbotForm!: FormGroup;
  bot: ResponseGetBotInterface | undefined;
  botName: string = '';
  botIdentifier: string = '';

  constructor(
    private readonly botService: BotService,
    private readonly aRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private messageService: MessageService,
    private globalService: GlobalObservableService
  ) {
    let origin = window.location.origin;
    this.botId = this.aRoute.snapshot.paramMap.get('token') ?? '';
    this.widgetUrl = this.widgetUrl + this.botId;
    if (this.botId.length === 0) {
      const sections = this.router.routerState.snapshot.url.split('/');
      this.botId = sections[3];
    }
    this.widgetUrl = this.widgetUrl + this.botId + `?o=${origin}`;
  }
  ngOnInit() {
    this.chatbotForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(20)]],
      identifier: ['', [Validators.maxLength(20)]],
    });
    this.getBotInfo();
    this.getWidgetConfig();
  }

  ngOnDestroy() {
    if (this.botSubscription) {
      this.botSubscription.unsubscribe();
    }
    clearTimeout(this.justSavedTimeout);
    this.destroy$.next();
    this.destroy$.complete();
  }
  ngAfterViewInit() {
    if (this.iframe?.nativeElement) {
      this.iframe.nativeElement.onload = () => {
        this.isIframeLoading = false;
      };
    }
  }

  getLabelText(key: string): string {
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
  }

  trackByColorRowIndex(index: number): number {
    return index;
  }

  trackByColorValue(_index: number, color: { value: string; name: string }): string {
    return color.value;
  }

  acceptStyle(_styleName: string, _style: string) {
    this.styles.botBubbleColor = this.tempStyles.botBubbleColor;
  }

  updateBotForm(data: any, id: string) {
    return this.botService
      .updateBot(data, id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (_value) => {
          this.messageService.add({
            key: 'tst',
            severity: 'success',
            summary: 'Acción exitosa',
            detail: '¡Los cambios se aplicaron!',
          });
        },
        error: (_err) => {
          this.messageService.add({
            key: 'tst',
            severity: 'error',
            summary: 'Cambio Fallido',
            detail: 'Cambio Fallido',
          });
        },
      });
  }

  saveConfig() {
    const nameValue = this.chatbotForm.get('name')?.value;
    const identifierValue = this.chatbotForm.get('identifier')?.value;

    const name = nameValue?.trim() ?? '';
    const identifier = identifierValue?.trim() ?? '';

    if (name.length > 20 || identifier.length > 20) {
      this.chatbotForm.markAllAsTouched();
      return;
    }

    if (this.chatbotForm.invalid || !name) {
      this.chatbotForm.markAllAsTouched();
      this.messageService.add({
        key: 'tst',
        severity: 'error',
        summary: 'Campo requerido',
        detail: 'El nombre no puede estar vacío',
      });
      return;
    }

    if (this.welcomeEnabled && !this.welcomeMessage.trim()) {
      this.welcomeMessageError = true;
      return;
    }
    this.welcomeMessageError = false;

    this.isLoading = true;
    this.showiframe = false;

    const chatbotWidgetConfig: ChatbotWidgetConfiguration = {
      name: name.replace(/[^a-zA-Z0-9À-ÿ\s.,-]/g, ''),
      identifier: identifier,
      color: this.selectedColor,
    };

    this.botService
      .updateBot(chatbotWidgetConfig, this.botId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.botService
            .saveWidgetConfig(this.botId, chatbotWidgetConfig)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.botService
                  .updateWelcomeMessage(this.botId, {
                    enabled: this.welcomeEnabled,
                    message: this.welcomeMessage,
                  })
                  .pipe(takeUntil(this.destroy$))
                  .subscribe({
                    next: () => {
                      this.savedState = {
                        name: chatbotWidgetConfig.name,
                        identifier: chatbotWidgetConfig.identifier,
                        color: this.selectedColor,
                        welcomeEnabled: this.welcomeEnabled,
                        welcomeMessage: this.welcomeMessage,
                      };
                      this.justSaved = true;
                      clearTimeout(this.justSavedTimeout);
                      this.justSavedTimeout = setTimeout(() => (this.justSaved = false), 3000);
                      this.globalService.publish('botName', { name });
                      this.globalService.publish('botIdentifier', { identifier });
                      setTimeout(() => {
                        this.isLoading = false;
                        this.showiframe = true;
                      }, 500);
                      this.messageService.add({
                        key: 'tst',
                        severity: 'success',
                        summary: 'Acción exitosa',
                        detail: '¡Los cambios se aplicaron!',
                      });
                    },
                    error: this.handleError.bind(this),
                  });
              },
              error: this.handleError.bind(this),
            });
        },
        error: this.handleError.bind(this),
      });
  }

  onWelcomeToggle() {
    if (!this.welcomeEnabled) {
      this.welcomeMessageError = false;
    }
  }

  private handleError(_error: any) {
    this.isLoading = false;
    this.showiframe = true;
    this.messageService.add({
      key: 'tst',
      severity: 'error',
      summary: 'Cambio Fallido',
      detail: 'Ocurrió un error al guardar los cambios',
    });
  }

  getBotInfo() {
    this.botSubscription = this.botService
      .getBot$(this.botId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value) => {
          if (!value) return;

          this.botName = value.name;
          this.botIdentifier = value.identifier;
          this.bot = value;
          this.chatbotForm.patchValue({
            name: value.name,
            identifier: value.identifier,
          });
          this.welcomeEnabled = value.enabled_welcome_message ?? false;
          this.welcomeMessage = value.welcome_message ?? '';
          this.savedState.name = value.name ?? '';
          this.savedState.identifier = value.identifier ?? '';
          this.savedState.welcomeEnabled = value.enabled_welcome_message ?? false;
          this.savedState.welcomeMessage = value.welcome_message ?? '';
          localStorage.setItem('bot', JSON.stringify(value));
        },
      });
  }
  getWidgetConfig() {
    this.botService
      .getWidget(this.botId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (widgetConfig) => {
          this.selectedColor = widgetConfig.configuration.color;
          this.savedState.color = widgetConfig.configuration.color;
        },
        error: (err) => {
          console.error('Failed to fetch widget config', err);
        },
      });
  }
}
